import { getAnnouncements } from '@/api/announcements';
import {
  AnnouncementData,
  AnnouncementStatus,
} from '@/api/announcements/types';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { listMockAnnouncements, USE_MOCK } from './mocks';

const statusPriority: Record<AnnouncementStatus, number> = {
  active: 0,
  draft: 1,
  inactive: 2,
};

function sortByStatusAndRecency(list: AnnouncementData[]): AnnouncementData[] {
  const recency = (item: AnnouncementData): number => {
    const date =
      item.status === 'draft'
        ? item.created_at
        : item.published_at ?? item.created_at;
    return date ? new Date(date).getTime() : 0;
  };

  return [...list].sort(
    (a, b) =>
      statusPriority[a.status] - statusPriority[b.status] ||
      recency(b) - recency(a)
  );
}

interface UseAnnouncementsHook {
  announcements: AnnouncementData[];
  isLoading: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  perPage: number;
  records: number;
  handleNewAnnouncement: () => void;
  handleGoToDetails: (id: number) => void;
}

export default function useAnnouncements(): UseAnnouncementsHook {
  const [announcements, setAnnouncements] = useState<AnnouncementData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState(0);
  const perPage = 10;

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();
  const navigate = useNavigate();

  const fetchAnnouncements = useCallback(async () => {
    setIsLoading(true);
    try {
      if (USE_MOCK) {
        const mockList = listMockAnnouncements();
        const sorted = sortByStatusAndRecency(mockList);
        setAnnouncements(sorted.slice((page - 1) * perPage, page * perPage));
        setRecords(mockList.length);
        return;
      }

      const response = await getAnnouncements(undefined, page, perPage);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (!response.data) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setAnnouncements(sortByStatusAndRecency(response.data));
      setRecords(response.detail.total_records ?? response.data.length);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setIsLoading(false);
    }
  }, [handleSnackbar, getErrorMessage, page]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleNewAnnouncement = useCallback(() => {
    navigate('/announcements/new');
  }, [navigate]);

  const handleGoToDetails = useCallback(
    (id: number) => {
      navigate(`/announcements/${id}`);
    },
    [navigate]
  );

  return {
    announcements,
    isLoading,
    page,
    setPage,
    perPage,
    records,
    handleNewAnnouncement,
    handleGoToDetails,
  };
}
